import ColorString from 'color-string'

export class Point {
  x = 0
  y = 0

  constructor(x: number, y: number) {
    this.x = x
    this.y = y
  }
}

export class Color {
  hex = '#ffffff'

  constructor(hex: string) {
    this.hex = hex
  }

  get rgbArray() {
    const arr = ColorString.get.rgb(this.hex)
    return arr ? arr.slice(0, 3) : [0, 0, 0]
  }
}

export class Triangle {
  points: [Point, Point, Point] = [new Point(0, 0), new Point(0, 0), new Point(0, 0)]
  color = new Color('#ffffff')

  constructor(values: Partial<Triangle> = {}) {
    if (values.points !== undefined) this.points = values.points
    if (values.color !== undefined) this.color = values.color
  }
}

export class RadialStripe {
  maxWidth = 3200
  maxHeight = 1800
  minUnitAngle = 5
  maxColors = 4
  maxFps = 60

  width = 160 * 8
  height = 90 * 8

  horizontalCenter = 0.5
  verticalCenter = 0.8
  colors: Color[] = ['#eff9fe', '#37a8de', '#cd74ac', '#f5b084'].map((hex) => new Color(hex))
  unitAngle = 10

  anglePerSec = 2
  fps = 30

  constructor(values: Partial<RadialStripe> = {}) {
    if (values.maxWidth !== undefined) this.maxWidth = values.maxWidth
    if (values.maxHeight !== undefined) this.maxHeight = values.maxHeight
    if (values.minUnitAngle !== undefined) this.minUnitAngle = values.minUnitAngle
    if (values.maxColors !== undefined) this.maxColors = values.maxColors
    if (values.maxFps !== undefined) this.maxFps = values.maxFps

    if (values.width !== undefined) this.width = values.width
    if (values.height !== undefined) this.height = values.height
    if (values.horizontalCenter !== undefined) this.horizontalCenter = values.horizontalCenter
    if (values.verticalCenter !== undefined) this.verticalCenter = values.verticalCenter
    if (values.colors !== undefined) this.colors = values.colors.map((c) => new Color(c.hex))
    if (values.unitAngle !== undefined) this.unitAngle = values.unitAngle
    if (values.anglePerSec !== undefined) this.anglePerSec = values.anglePerSec
    if (values.fps !== undefined) this.fps = values.fps
  }

  get limitedWidth() {
    return Math.min(this.width, this.maxWidth)
  }

  get limitedHeight() {
    return Math.min(this.height, this.maxHeight)
  }

  get limitedFps() {
    return Math.min(this.fps, this.maxFps)
  }

  get triangles(): Triangle[] {
    return this.generateTriangles(0)
  }

  generateTriangles(tilt = 0): Triangle[] {
    const unitAngle = Math.max(this.unitAngle, this.minUnitAngle)
    const count = Math.ceil(360 / unitAngle)
    const cx = this.limitedWidth * this.horizontalCenter,
      cy = this.limitedHeight * this.verticalCenter
    const radiuses = [
      Math.sqrt(Math.pow(cx, 2) + Math.pow(cy, 2)),
      Math.sqrt(Math.pow(cx, 2) + Math.pow(this.limitedHeight - cy, 2)),
      Math.sqrt(Math.pow(this.limitedWidth - cx, 2) + Math.pow(cy, 2)),
      Math.sqrt(Math.pow(this.limitedWidth - cx, 2) + Math.pow(this.limitedHeight - cy, 2)),
    ]
    const maxRadius = Math.max(...radiuses)
    const colors = this.colors.slice(0, this.maxColors)

    const triangles: Triangle[] = []
    for (let i = 0; i < count; i++) {
      const angle = i * unitAngle + tilt
      const fromRad = (Math.PI * angle) / 180
      const toRad = (Math.PI * (angle + unitAngle) + unitAngle * 0.05) / 180
      const p0 = new Point(cx, cy),
        p1 = new Point(cx + Math.cos(fromRad) * maxRadius, cy + Math.sin(fromRad) * maxRadius),
        p2 = new Point(cx + Math.cos(toRad) * maxRadius, cy + Math.sin(toRad) * maxRadius)
      const t = new Triangle({
        points: [p0, p1, p2],
        color: colors[i % colors.length],
      })
      triangles.push(t)
    }

    return triangles
  }
}

export class ScreenIndicator {
  loading = false
  infomation = ''
  error = ''

  reset() {
    this.loading = false
    this.infomation = ''
    this.error = ''
  }

  async withLoading(cb: () => Promise<any>) {
    this.reset()
    this.loading = true
    try {
      await cb()
    } catch (ex) {
      this.error = ex.message
    } finally {
      this.loading = false
    }
  }
}
